// Route guards (SM-722). Every screen route declares the capability its
// reads need; its beforeLoad re-checks it against the selected node's
// capabilities on every navigation (the shell re-reads memberships when
// stale). A forbidden screen does NOT redirect and does NOT go blank: it
// calls the screen's primary endpoint anyway and renders the SERVER's
// refusal, so the words on screen are the authority's. If the server
// answers after all (the cached capabilities were stale), the memberships
// are re-read and the screen renders.
import { useEffect, type ReactNode } from "react";
import { useQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { guardRoute, type GuardDecision } from "@/lib/capabilities";
import { keys } from "@/lib/queries";
import type { CurrencyCode } from "@/lib/currency";
import type { AuthMe, StaffCapability, StaffMeResponse } from "@/lib/types";
import type { Scope } from "@/components/layout/ScopeContext";
import { ErrorState, LoadingState } from "@/components/ui/States";
import { useI18n } from "@/i18n/I18nProvider";

export interface ShellSearch {
  node?: string;
  currency?: CurrencyCode;
}

export type Shell =
  | { kind: "ready"; scope: Scope }
  | { kind: "not-staff"; me: AuthMe; error: unknown }
  | { kind: "no-active"; me: AuthMe; staff: StaffMeResponse }
  | { kind: "node-error"; me: AuthMe; staff: StaffMeResponse; nodeId: string; currency: CurrencyCode; error: unknown }
  | { kind: "error"; me: AuthMe; error: unknown };

/** A child route's beforeLoad: the guard decision for its capability. */
export function capabilityGuard(requires: StaffCapability) {
  return ({ context }: { context: { shell: Shell } }): { guard: GuardDecision } => ({
    guard:
      context.shell.kind === "ready"
        ? guardRoute(context.shell.scope.capabilities, requires)
        : { kind: "forbidden", requires },
  });
}

export interface Probe {
  queryKey: QueryKey;
  queryFn: () => Promise<unknown>;
}

export function Guarded({ guard, probe, children }: { guard: GuardDecision; probe: Probe; children: ReactNode }) {
  if (guard.kind === "allow") return <>{children}</>;
  return <ForbiddenProbe requires={guard.requires} probe={probe}>{children}</ForbiddenProbe>;
}

function ForbiddenProbe({ requires, probe, children }: { requires: StaffCapability; probe: Probe; children: ReactNode }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const router = useRouter();
  const q = useQuery({ queryKey: probe.queryKey, queryFn: probe.queryFn, retry: false });

  useEffect(() => {
    if (q.isSuccess) {
      // The server allowed it: our capabilities were stale. Re-read them.
      void qc.invalidateQueries({ queryKey: keys.staffMe() }).then(() => router.invalidate());
    }
  }, [q.isSuccess, qc, router]);

  if (q.isSuccess) return <>{children}</>;
  return (
    <section aria-labelledby="forbidden-title" className="space-y-3">
      <h1 id="forbidden-title" className="page-title">
        {t("guard.title")}
      </h1>
      <p className="page-subtitle">{t("guard.body", { capability: t(`capability.${requires}`) })}</p>
      {q.isLoading ? <LoadingState lines={2} /> : <ErrorState error={q.error} />}
    </section>
  );
}
